// The canonical-URL model (ADR-0053 §1, §3). A pure `(path, siteUrl) → absolute
// URL` used by every head tag that must be absolute (`canonical`, `og:url`,
// `og:image`). It replaces the old `window.location`-derived URLs, which are
// wrong/relative under the build-time prerender and invisible to non-JS social
// scrapers. The absolute base comes from a per-environment build-time `SITE_URL`
// (see `siteUrl.ts`), so staging bakes staging URLs and prod bakes `pufimi.pl`.
//
// Canonical host rules (ADR-0053 §3): the apex host (301 `www` → apex, so `www`
// is normalised away here too — never emit a `www` canonical), and NO trailing
// slash. Kept pure and side-effect-free so it is unit-testable and identical
// under prerender, hydration, and client navigation.

/** The apex origin of a site URL: its protocol + host with a leading `www.`
 *  stripped and any path/query/hash discarded. `https://www.pufimi.pl/x` →
 *  `https://pufimi.pl`. A non-`www` subdomain (e.g. `staging.`) is preserved. */
function apexOrigin(siteUrl: string): string {
  const url = new URL(siteUrl);
  url.hostname = url.hostname.replace(/^www\./i, "");
  return `${url.protocol}//${url.host}`;
}

/**
 * Resolve a root-relative path against `siteUrl` into an absolute, apex-host,
 * trailing-slash-free URL.
 *
 * - `("/", "https://pufimi.pl")` → `"https://pufimi.pl"`
 * - `("/comparison", "https://www.pufimi.pl/")` → `"https://pufimi.pl/comparison"`
 * - `("/landing/hero.jpg", "https://staging.pufimi.pl")` →
 *   `"https://staging.pufimi.pl/landing/hero.jpg"`
 *
 * A path missing its leading slash is treated as root-relative. An absolute
 * `http(s)` URL passed as `path` is returned normalised to the apex host (so an
 * asset already pointing at a full URL can flow through unchanged in spirit).
 */
export function canonicalUrl(path: string, siteUrl: string): string {
  const origin = apexOrigin(siteUrl);
  const relative = /^https?:\/\//i.test(path) ? path : `/${path.replace(/^\/+/, "")}`;
  const url = new URL(relative, `${origin}/`);
  url.hostname = url.hostname.replace(/^www\./i, "");
  // Drop a single trailing slash so `/` maps to the bare origin and every other
  // path is slash-free — but never mangle the path when a query/hash is present.
  if (!url.search && !url.hash && url.pathname !== "/" && url.pathname.endsWith("/")) {
    url.pathname = url.pathname.replace(/\/+$/, "");
  }
  const out = url.toString();
  return url.pathname === "/" && !url.search && !url.hash ? out.replace(/\/$/, "") : out;
}
